<template>
  <div>
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>订单管理</span>
        <div class="card-header__actions">
          <el-button @click="pickupCodeDialogVisible = true">提货码核销</el-button>
          <el-button :loading="loading" @click="loadData">刷新</el-button>
        </div>
      </div>

      <el-alert
        v-if="query.groupBuyActivityId"
        class="state-block"
        type="info"
        :closable="false"
        show-icon
        title="当前正在查看某个拼团活动关联的订单"
      >
        <template slot>
          <span>活动编号：{{ query.groupBuyActivityId }}</span>
          <el-button type="text" @click="clearGroupBuyFilter">清除筛选</el-button>
        </template>
      </el-alert>

      <div class="summary-row">
        <span class="summary-row__label">订单类型</span>
        <el-tag :effect="isActiveOrderType('') ? 'dark' : 'plain'" class="summary-tag" @click.native="setOrderTypeFilter('')">
          全部类型
        </el-tag>
        <el-tag type="info" :effect="isActiveOrderType(0) ? 'dark' : 'plain'" class="summary-tag" @click.native="setOrderTypeFilter(0)">
          普通订单
        </el-tag>
        <el-tag type="warning" :effect="isActiveOrderType(1) ? 'dark' : 'plain'" class="summary-tag" @click.native="setOrderTypeFilter(1)">
          团购订单
        </el-tag>
      </div>

      <div class="summary-row">
        <span class="summary-row__label">订单状态</span>
        <el-tag :effect="isActiveStatus('') ? 'dark' : 'plain'" class="summary-tag" @click.native="setStatusFilter('')">
          全部 {{ summary.all }}
        </el-tag>
        <el-tag type="warning" :effect="isActiveStatus(0) ? 'dark' : 'plain'" class="summary-tag" @click.native="setStatusFilter(0)">
          待付款 {{ summary.pendingPay }}
        </el-tag>
        <el-tag type="primary" :effect="isActiveStatus(1) ? 'dark' : 'plain'" class="summary-tag" @click.native="setStatusFilter(1)">
          待发货 {{ summary.pendingDelivery }}
        </el-tag>
        <el-tag type="success" :effect="isActiveStatus(2) ? 'dark' : 'plain'" class="summary-tag" @click.native="setStatusFilter(2)">
          待收货 / 待自提 {{ summary.pendingReceive }}
        </el-tag>
      </div>

      <el-form :inline="true" :model="query" class="query-form">
        <el-form-item>
          <el-input v-model.trim="query.orderNo" placeholder="订单号" clearable />
        </el-form-item>
        <el-form-item>
          <el-input v-model.trim="query.pickupCode" placeholder="提货码" clearable />
        </el-form-item>
        <el-form-item>
          <el-input v-model.trim="query.receiverMobile" placeholder="手机号" clearable />
        </el-form-item>
        <el-form-item>
          <el-select v-model="query.orderType" placeholder="订单类型" clearable>
            <el-option :value="0" label="普通订单" />
            <el-option :value="1" label="团购订单" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-select v-model="query.deliveryType" placeholder="发货方式" clearable>
            <el-option :value="1" label="物流快递" />
            <el-option :value="2" label="用户自提" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-select v-model="query.orderStatus" placeholder="订单状态" clearable>
            <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-select v-model="query.pickupStatus" placeholder="自提状态" clearable>
            <el-option v-for="item in pickupStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-select v-model="query.refundStatus" placeholder="退款状态" clearable>
            <el-option v-for="item in refundOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-select v-model="query.afterSaleStatus" placeholder="售后状态" clearable>
            <el-option v-for="item in afterSaleOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="resetQuery">重置</el-button>
          <el-button type="primary" @click="loadData">查询</el-button>
        </el-form-item>
      </el-form>

      <el-alert
        v-if="loadError"
        class="state-block"
        title="订单数据加载失败"
        type="error"
        :closable="false"
        show-icon
      >
        <template slot>
          <span>可以稍后重试，或先检查后端服务状态。</span>
          <el-button type="text" @click="loadData">重新加载</el-button>
        </template>
      </el-alert>

      <template v-else>
        <el-empty v-if="!loading && !list.length" description="没有匹配的订单数据" />

        <template v-else>
          <el-table :data="list" border v-loading="loading">
            <el-table-column label="订单号" min-width="300">
              <template slot-scope="{ row }">
                <div class="order-no-cell">
                  <div>{{ row.orderNo }}</div>
                  <div class="order-no-cell__meta">
                    <el-tag size="mini" :type="Number(row.orderType) === 1 ? 'warning' : 'info'">
                      {{ Number(row.orderType) === 1 ? '团购单' : '普通单' }}
                    </el-tag>
                    <el-tag size="mini" :type="deliveryTypeTagType(row.deliveryType)">
                      {{ deliveryTypeText(row.deliveryType) }}
                    </el-tag>
                    <el-tag v-if="row.refundStatus" size="mini" :type="refundTagType(row.refundStatus)">
                      {{ refundText(row.refundStatus) }}
                    </el-tag>
                    <el-tag v-if="row.afterSaleStatus" size="mini" type="danger">
                      {{ afterSaleText(row.afterSaleStatus) }}
                    </el-tag>
                    <span v-if="row.groupBuyRecordId" class="order-no-cell__record">拼团编号 {{ row.groupBuyRecordId }}</span>
                  </div>
                  <div v-if="row.pickupCode" class="order-no-cell__pickup">提货码 {{ row.pickupCode }}</div>
                  <div v-if="row.refundNo" class="order-no-cell__refund">退款单号 {{ row.refundNo }}</div>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="receiverName" label="收货人" width="120" />
            <el-table-column prop="receiverMobile" label="手机号" width="140" />
            <el-table-column label="订单金额" width="120">
              <template slot-scope="{ row }">￥{{ row.payAmountText }}</template>
            </el-table-column>
            <el-table-column label="订单状态" width="130">
              <template slot-scope="{ row }">
                <el-tag :type="statusType(row.orderStatus)">{{ statusText(row.orderStatus, row.deliveryType, row.pickupStatus) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="自提状态" width="120">
              <template slot-scope="{ row }">
                <span v-if="Number(row.deliveryType) !== 2" class="text-muted">-</span>
                <el-tag v-else size="mini" type="warning">{{ pickupStatusText(row.pickupStatus) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="微信发货同步" width="120">
              <template slot-scope="{ row }">
                <template v-if="hasWxShippingSync(row)">
                  <el-tag size="mini" :type="wxShippingStatusType(row.wxShippingSyncStatus)">
                    {{ wxShippingStatusText(row.wxShippingSyncStatus) }}
                  </el-tag>
                </template>
                <span v-else class="text-muted">{{ wxShippingPlaceholder(row) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="团购状态" width="150">
              <template slot-scope="{ row }">
                <template v-if="Number(row.orderType) === 1">
                  <el-tag size="mini" :type="groupBuyStatusType(row.groupBuyStatus)">
                    {{ groupBuyStatusText(row.groupBuyStatus) }}
                  </el-tag>
                  <div class="sub-text">{{ Number(row.groupBuyJoinedPeople || 0) }} / {{ Number(row.groupBuyRequiredPeople || 0) }} 人</div>
                </template>
                <span v-else class="text-muted">-</span>
              </template>
            </el-table-column>
            <el-table-column label="退款状态" width="120">
              <template slot-scope="{ row }">
                <span v-if="!row.refundStatus" class="text-muted">无退款</span>
                <el-tag v-else :type="refundTagType(row.refundStatus)">{{ refundText(row.refundStatus) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="createTimeText" label="创建时间" min-width="180" />
            <el-table-column label="操作" width="220" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" @click="goDetail(row.orderNo)">查看详情</el-button>
                <el-button
                  v-if="canPreparePickup(row)"
                  type="text"
                  @click="confirmPickupReady(row)"
                >
                  确认备货
                </el-button>
                <el-button
                  v-if="canFinishPickup(row)"
                  type="text"
                  @click="confirmPickupFinish(row)"
                >
                  确认提货
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="table-footer">
            <div class="table-footer__meta">共 {{ total }} 条</div>
            <el-pagination
              background
              layout="prev, pager, next, sizes"
              :current-page="query.pageNum"
              :page-sizes="[10, 20, 50]"
              :page-size="query.pageSize"
              :total="total"
              @current-change="changePage"
              @size-change="changePageSize"
            />
          </div>
        </template>
      </template>
    </el-card>

    <el-dialog
      title="提货码核销"
      :visible.sync="pickupCodeDialogVisible"
      width="420px"
      :close-on-click-modal="false"
    >
      <el-form label-width="80px">
        <el-form-item label="提货码">
          <el-input
            v-model.trim="pickupCodeForm.pickupCode"
            maxlength="20"
            show-word-limit
            placeholder="请输入用户提供的提货码"
            @keyup.enter.native="submitPickupCodeFinish"
          />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="closePickupCodeDialog">取消</el-button>
        <el-button type="primary" :loading="pickupCodeSubmitting" @click="submitPickupCodeFinish">确认核销</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { deliveryOrder, finishPickupOrder, finishPickupOrderByCode, getOrderPage, retryWechatShippingSync } from '../../api/admin'
import { formatDateTime, formatPrice } from '../../utils/format'

const ORDER_QUERY_KEY = 'mall_admin_order_query'

export default {
  data() {
    return {
      query: {
        orderNo: '',
        pickupCode: '',
        receiverMobile: '',
        orderType: '',
        deliveryType: '',
        orderStatus: '',
        pickupStatus: '',
        refundStatus: '',
        afterSaleStatus: '',
        groupBuyActivityId: '',
        pageNum: 1,
        pageSize: 20
      },
      list: [],
      total: 0,
      loading: false,
      loadError: false,
      pickupCodeDialogVisible: false,
      pickupCodeSubmitting: false,
      pickupCodeForm: {
        pickupCode: ''
      },
      summary: {
        all: 0,
        pendingPay: 0,
        pendingDelivery: 0,
        pendingReceive: 0
      },
      statusOptions: [
        { value: 0, label: '待付款' },
        { value: 1, label: '待发货' },
        { value: 2, label: '待收货 / 待自提' },
        { value: 3, label: '已完成' },
        { value: 4, label: '已取消' }
      ],
      pickupStatusOptions: [
        { value: 0, label: '待备货' },
        { value: 1, label: '待自提' },
        { value: 2, label: '已提货' }
      ],
      refundOptions: [
        { value: 1, label: '退款中' },
        { value: 2, label: '退款成功' },
        { value: 3, label: '退款失败' }
      ],
      afterSaleOptions: [
        { value: 1, label: '已申请' },
        { value: 2, label: '处理中' },
        { value: 3, label: '已处理' }
      ]
    }
  },
  created() {
    this.restoreQuery()
    this.loadData()
  },
  methods: {
    statusText(status, deliveryType, pickupStatus) {
      if (Number(deliveryType) === 2) {
        if (Number(status) === 1 && Number(pickupStatus || 0) === 0) {
          return '待备货'
        }
        if (Number(status) === 2) {
          return '待自提'
        }
      }
      const map = { 0: '待付款', 1: '待发货', 2: '待收货', 3: '已完成', 4: '已取消' }
      return map[Number(status)] || '未知状态'
    },
    pickupStatusText(status) {
      const map = { 0: '待备货', 1: '待自提', 2: '已提货' }
      return map[Number(status || 0)] || '待备货'
    },
    wxShippingStatusText(status) {
      const map = { 0: '待同步', 1: '同步成功', 2: '失败待重试', 3: '需人工处理' }
      return map[Number(status)] || '待同步'
    },
    wxShippingStatusType(status) {
      const map = { 0: 'info', 1: 'success', 2: 'warning', 3: 'danger' }
      return map[Number(status)] || 'info'
    },
    wxShippingTriggerText(triggerType) {
      const map = { 1: '快递发货触发', 2: '自提备货触发' }
      return map[Number(triggerType)] || '发货事件'
    },
    deliveryTypeText(deliveryType) {
      return Number(deliveryType) === 2 ? '用户自提' : '物流快递'
    },
    deliveryTypeTagType(deliveryType) {
      return Number(deliveryType) === 2 ? 'warning' : 'success'
    },
    statusType(status) {
      const map = { 0: 'warning', 1: 'primary', 2: 'success', 3: '', 4: 'info' }
      return map[Number(status)] || 'info'
    },
    refundText(status) {
      const map = { 1: '退款中', 2: '退款成功', 3: '退款失败' }
      return map[Number(status)] || '无退款'
    },
    refundTagType(status) {
      const map = { 1: 'warning', 2: 'success', 3: 'danger' }
      return map[Number(status)] || 'info'
    },
    afterSaleText(status) {
      const map = { 1: '售后已申请', 2: '售后处理中', 3: '售后已处理' }
      return map[Number(status)] || '售后'
    },
    groupBuyStatusText(status) {
      const map = { 0: '拼团中', 1: '已成团', 2: '已关闭' }
      return map[Number(status)] || '未创建'
    },
    groupBuyStatusType(status) {
      const map = { 0: 'warning', 1: 'success', 2: 'info' }
      return map[Number(status)] || 'info'
    },
    isActiveOrderType(type) {
      return this.query.orderType === type
    },
    isActiveStatus(status) {
      return this.query.orderStatus === status
    },
    canPreparePickup(row) {
      return Number(row.deliveryType) === 2 && Number(row.orderStatus) === 1 && Number(row.pickupStatus || 0) === 0
    },
    canFinishPickup(row) {
      return Number(row.deliveryType) === 2 && Number(row.orderStatus) === 2 && Number(row.pickupStatus || 0) === 1
    },
    hasWxShippingSync(row) {
      return !!row.wxShippingSyncId
    },
    canRetryWxShipping(row) {
      return !!row.wxShippingSyncId && [2, 3].includes(Number(row.wxShippingSyncStatus))
    },
    wxShippingPlaceholder(row) {
      if (Number(row.payStatus) !== 1) {
        return '未支付'
      }
      if (Number(row.deliveryType) === 2 && Number(row.pickupStatus || 0) === 0) {
        return '备货完成后同步'
      }
      if (Number(row.deliveryType) !== 2 && Number(row.orderStatus) === 1) {
        return '发货后同步'
      }
      return '暂无同步记录'
    },
    shortenText(text, maxLength = 24) {
      if (!text) {
        return ''
      }
      return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text
    },
    setOrderTypeFilter(type) {
      this.query.orderType = type
      this.query.pageNum = 1
      this.loadData()
    },
    setStatusFilter(status) {
      this.query.orderStatus = status
      this.query.pageNum = 1
      this.loadData()
    },
    async loadData() {
      this.loading = true
      this.loadError = false
      try {
        const data = await getOrderPage(this.query)
        this.list = (data.list || []).map(item => ({
          ...item,
          createTimeText: formatDateTime(item.createTime),
          payAmountText: formatPrice(item.payAmount),
          wxShippingSyncTimeText: formatDateTime(item.wxShippingSyncTime)
        }))
        this.total = data.total || 0
      } catch (error) {
        this.loadError = true
      } finally {
        this.loading = false
      }
      await this.loadSummary()
      this.persistQuery()
    },
    persistQuery() {
      sessionStorage.setItem(ORDER_QUERY_KEY, JSON.stringify(this.query))
    },
    restoreQuery() {
      const routeQuery = this.$route.query.q || sessionStorage.getItem(ORDER_QUERY_KEY)
      if (!routeQuery) {
        return
      }
      try {
        const parsed = JSON.parse(routeQuery)
        this.query = { ...this.query, ...parsed }
      } catch (error) {
        sessionStorage.removeItem(ORDER_QUERY_KEY)
      }
    },
    async loadSummary() {
      const baseQuery = {
        pageNum: 1,
        pageSize: 1,
        orderType: this.query.orderType,
        deliveryType: this.query.deliveryType,
        groupBuyActivityId: this.query.groupBuyActivityId || ''
      }
      try {
        const [all, pendingPay, pendingDelivery, pendingReceive] = await Promise.all([
          getOrderPage(baseQuery),
          getOrderPage({ ...baseQuery, orderStatus: 0 }),
          getOrderPage({ ...baseQuery, orderStatus: 1 }),
          getOrderPage({ ...baseQuery, orderStatus: 2 })
        ])
        this.summary = {
          all: all.total || 0,
          pendingPay: pendingPay.total || 0,
          pendingDelivery: pendingDelivery.total || 0,
          pendingReceive: pendingReceive.total || 0
        }
      } catch (error) {
        this.summary = {
          all: this.total || 0,
          pendingPay: 0,
          pendingDelivery: 0,
          pendingReceive: 0
        }
      }
    },
    resetQuery() {
      this.query = {
        orderNo: '',
        pickupCode: '',
        receiverMobile: '',
        orderType: '',
        deliveryType: '',
        orderStatus: '',
        pickupStatus: '',
        refundStatus: '',
        afterSaleStatus: '',
        groupBuyActivityId: '',
        pageNum: 1,
        pageSize: 20
      }
      this.loadData()
    },
    clearGroupBuyFilter() {
      this.query.groupBuyActivityId = ''
      this.query.pageNum = 1
      this.loadData()
    },
    changePage(page) {
      this.query.pageNum = page
      this.loadData()
    },
    changePageSize(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    async confirmPickupReady(row) {
      try {
        await this.$confirm(`确认将订单 ${row.orderNo} 标记为备货完成吗？`, '确认备货完成', { type: 'warning' })
      } catch (error) {
        return
      }
      await deliveryOrder({
        orderNo: row.orderNo,
        deliveryType: row.deliveryType,
        remark: ''
      })
      this.$message.success('已确认备货完成')
      await this.loadData()
    },
    async confirmPickupFinish(row) {
      try {
        await this.$confirm(`确认将订单 ${row.orderNo} 标记为提货完成吗？`, '确认提货完成', { type: 'warning' })
      } catch (error) {
        return
      }
      await finishPickupOrder(row.orderNo)
      this.$message.success('已确认提货完成')
      await this.loadData()
    },
    async retryWxShipping(row) {
      try {
        await this.$confirm(`确认重试订单 ${row.orderNo} 的微信发货同步吗？`, '重试微信发货同步', { type: 'warning' })
      } catch (error) {
        return
      }
      await retryWechatShippingSync(row.wxShippingSyncId)
      this.$message.success('已提交重试')
      await this.loadData()
    },
    async submitPickupCodeFinish() {
      if (!this.pickupCodeForm.pickupCode) {
        this.$message.warning('请输入提货码')
        return
      }
      this.pickupCodeSubmitting = true
      try {
        await finishPickupOrderByCode(this.pickupCodeForm.pickupCode)
        this.$message.success('提货码核销成功')
        this.closePickupCodeDialog()
        await this.loadData()
      } finally {
        this.pickupCodeSubmitting = false
      }
    },
    closePickupCodeDialog() {
      this.pickupCodeDialogVisible = false
      this.pickupCodeForm.pickupCode = ''
    },
    goDetail(orderNo) {
      this.$router.push({
        path: `/orders/${orderNo}`,
        query: { q: JSON.stringify(this.query) }
      })
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header__actions {
  display: flex;
  gap: 12px;
}

.summary-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.summary-row__label {
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
}

.summary-tag {
  cursor: pointer;
}

.query-form {
  margin-bottom: 12px;
}

.state-block {
  margin-bottom: 16px;
}

.order-no-cell__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.order-no-cell__record,
.order-no-cell__refund,
.order-no-cell__pickup,
.text-muted,
.sub-text {
  color: #8b7a68;
  font-size: 12px;
}

.order-no-cell__pickup,
.order-no-cell__refund {
  margin-top: 6px;
}

.sub-text {
  margin-top: 4px;
}

.retry-link {
  padding: 0;
  margin-top: 4px;
}

.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
}

.table-footer__meta {
  color: #6b7280;
  font-size: 13px;
}
</style>
